/**
 * Main App Component
 * Entry point for the React application
 */

import React, { useState, useRef, useEffect } from 'react';
import RecommendationForm, { SERVICES, MOODS, COUNTRIES } from './components/RecommendationForm';
import RecommendationCard from './components/RecommendationCard';
import LoadingSpinner from './components/LoadingSpinner';
import { AboutPage, PrivacyPage } from './InfoPages';
import { getAnalyticsChoice, setAnalyticsChoice, newRequestId, track } from './analytics';
import './styles/App.css';

const allowedServices = new Set(SERVICES.map(s => s.id));
const allowedMoods = new Set(MOODS);
const allowedCountries = new Set(COUNTRIES.map(c => c.code));
function safePreferences(value) {
    if (!value || typeof value !== 'object') return null;
    const moods = Array.isArray(value.moods) ? value.moods.filter(m => allowedMoods.has(m)) : [];
    const services = Array.isArray(value.services) ? value.services.filter(s => allowedServices.has(s)) : [];
    const timeAvailable = [15, 30, 60, 120, 999].includes(Number(value.timeAvailable)) ? Number(value.timeAvailable) : 120;
    const country = allowedCountries.has(value.country) ? value.country : null;
    return { moods, services: services.length ? services : ['netflix'], timeAvailable, country };
}
function initialPreferences() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('services') || params.has('moods') || params.has('time')) return safePreferences({ moods: params.get('moods')?.split(',') || [], services: params.get('services')?.split(',') || [], timeAvailable: params.get('time'), country: params.get('country') });
    try { return safePreferences(JSON.parse(localStorage.getItem('wtfnetflix-preferences') || 'null')); } catch { return null; }
}

function App() {
    const path = window.location.pathname;
    const [formInitial, setFormInitial] = useState(initialPreferences);
    const [formKey, setFormKey] = useState(0);
    const [analyticsChoice, setChoice] = useState(getAnalyticsChoice);
    const [shareUrl, setShareUrl] = useState('');
    const [resultRequestId, setResultRequestId] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [lastPreferences, setLastPreferences] = useState(null);
    const [seenKeys, setSeenKeys] = useState(() => { try { return JSON.parse(localStorage.getItem('wtfnetflix-seen') || '[]'); } catch { return []; } });
    const [resultState, setResultState] = useState(null);
    const [fixture, setFixture] = useState(false);
    const resultsRef = useRef(null);

    // Scroll to results when they change
    useEffect(() => {
        if (recommendations.length > 0 && resultsRef.current) {
            // Small timeout to ensure DOM is ready
            setTimeout(() => {
                resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [recommendations]);

    /**
     * Handle recommendation request
     * @param {object} preferences - User preferences from form
     */
    const handleGetRecommendations = async (preferences, shuffle = false, overrideSeen = null) => {
        const requestId = newRequestId();
        if (shuffle) track('another_clicked', { requestId });
        track('picker_submitted', { requestId });
        setLoading(true);
        setError(null);
        setHasSearched(true);
        setLastPreferences(preferences);
        setRecommendations([]);
        setResultState(null);
        const excluded = overrideSeen !== null ? overrideSeen : shuffle ? [...new Set([...seenKeys, ...recommendations.map(r => r.identity)])].slice(-200) : seenKeys;
        if (shuffle) { setSeenKeys(excluded); localStorage.setItem('wtfnetflix-seen', JSON.stringify(excluded)); }

        try {
            const response = await fetch('/api/recommend', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ...preferences, seen: excluded }),
            });

            const data = await response.json();

            if (data.success) {
                setRecommendations(data.recommendations);
                setResultState(data.state);
                setFixture(!!data.fixture);
                setResultRequestId(requestId);
                if (data.recommendations.length) track('recommendation_shown', { requestId });
                else track('no_match', { requestId });
            } else {
                setError(data.error || 'Failed to get recommendations');
                track('recommendation_error', { requestId });
            }
        } catch (err) {
            console.error('Request error:', err);
            setError('Failed to connect to server. Please try again.');
            track('recommendation_error', { requestId });
        } finally {
            setLoading(false);
        }
    };
    const handleSeen = (recommendation) => {
        track('seen_clicked', { requestId: resultRequestId, titleKey: recommendation.identity });
        const next = [...new Set([...seenKeys, recommendation.identity])].slice(-200);
        setSeenKeys(next);
        localStorage.setItem('wtfnetflix-seen', JSON.stringify(next));
        setRecommendations(previous => {
            const remaining = previous.filter(r => r.identity !== recommendation.identity);
            if (!remaining.length) setResultState('exhausted');
            return remaining;
        });
    };
    const resetPreferences = () => {
        try { localStorage.removeItem('wtfnetflix-preferences'); localStorage.removeItem('wtfnetflix-seen'); } catch {}
        setSeenKeys([]);
        setFormInitial(null);
        setFormKey(value => value + 1);
        setRecommendations([]);
        setHasSearched(false);
        setShareUrl('');
        window.history.replaceState({}, '', '/');
    };
    const sharePreferences = async (preferences) => {
        const url = new URL('/', window.location.origin);
        if (preferences.moods.length) url.searchParams.set('moods', preferences.moods.join(','));
        url.searchParams.set('time', String(preferences.timeAvailable));
        url.searchParams.set('services', preferences.services.join(','));
        url.searchParams.set('country', preferences.country);
        try { await navigator.clipboard.writeText(url.toString()); setShareUrl('Filter link copied.'); }
        catch { setShareUrl(url.toString()); }
        track('share_clicked', { requestId: resultRequestId || newRequestId() });
    };
    const chooseAnalytics = choice => { setAnalyticsChoice(choice); setChoice(choice); };

    return (
        <div className={`app ${analyticsChoice === null ? 'has-analytics-notice' : ''}`}>
            {/* Header */}
            <header className="header">
                <div className="container">
                    <h1 className="title">
                        What the <span className="highlight">Fuck</span> Should I Watch? <sup className="beta-badge">Beta</sup>
                    </h1>
                    <p className="subtitle">
                        Get instant recommendations based on your mood and time available
                    </p>
                </div>
            </header>

            {/* Main Content */}
            <main className="main">
                {path === '/about' ? <div className="container"><AboutPage /></div> : path === '/privacy' ? <div className="container"><PrivacyPage analyticsChoice={analyticsChoice} onChoice={chooseAnalytics} /></div> :
                <div className="container">
                    {/* Recommendation Form */}
                    <RecommendationForm key={formKey} initialPreferences={formInitial} onSubmit={handleGetRecommendations} onShare={sharePreferences} loading={loading} />
                    <div className="picker-tools"><button type="button" onClick={resetPreferences}>Reset saved filters and seen titles</button>{shareUrl && <span role="status">{shareUrl}</span>}</div>

                    {/* Loading State */}
                    {loading && <LoadingSpinner />}

                    {/* Error State */}
                    {error && (
                        <div className="error-message">
                            <p>😕 {error}</p>
                        </div>
                    )}
                    {fixture && !loading && !error && <div className="form-hint">Fixture mode: synthetic test recommendations; availability is not live.</div>}

                    {/* Results */}
                    {!loading && recommendations.length > 0 && (
                        <div className="results" ref={resultsRef}>
                            <h2 className="results-title">
                                Here's what you should watch:
                            </h2>
                            <div className="recommendations-grid">
                                {recommendations.map((rec, index) => (
                                    <RecommendationCard key={rec.identity || index} recommendation={rec} onSeen={() => handleSeen(rec)} onWatch={() => track('watch_clicked', { requestId: resultRequestId, titleKey: rec.identity })} />
                                ))}
                            </div>

                            {/* Shuffle Button */}
                            <div className="actions-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '2rem' }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => handleGetRecommendations(lastPreferences, true)}
                                    style={{ margin: 0, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#333', border: '1px solid #555' }}
                                >
                                    🔀 Shuffle Results
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                                    style={{ margin: 0, height: '100%' }}
                                >
                                    Change Filters
                                </button>
                            </div>
                        </div>
                    )}

                    {/* No Results */}
                    {!loading && hasSearched && recommendations.length === 0 && !error && (
                        <div className="no-results" ref={resultsRef}>
                            <p>{resultState === 'exhausted' ? 'No unseen matches remain in this search pool. Change filters or clear seen titles.' : 'No confirmed subscription matches found. Try different criteria.'}</p>
                            {resultState === 'exhausted' && <button className="btn btn-secondary" onClick={() => { setSeenKeys([]); localStorage.removeItem('wtfnetflix-seen'); handleGetRecommendations(lastPreferences, false, []); }}>Clear seen titles</button>}
                        </div>
                    )}

                    <section className="about-picker" aria-labelledby="about-picker-title">
                        <h2 id="about-picker-title">Find something to watch on your streaming services</h2>
                        <p>
                            Stuck choosing a film or TV show? Pick your mood, how much time you have,
                            your streaming services and your country. The picker looks for subscription
                            options that match, so you can spend less time scrolling and more time watching.
                        </p>
                        <h3>How it works</h3>
                        <p>
                            We check title details for the running time and look for a matching streaming
                            offer in your country. For TV shows, the time shown is for an episode, not the
                            whole series. Use Shuffle Results for another pick, or mark a title as already
                            seen to help keep it out of later suggestions on this device.
                        </p>
                        <h3>Finding your pick</h3>
                        <p>
                            A direct viewing link is shown when one is available. Otherwise, “Find on”
                            takes you to a page for the matching service. Streaming catalogues can change,
                            so check the final listing before you settle in.
                        </p>
                        <h3>Start with a guide</h3>
                        <ul className="guide-links">
                            <li><a href="/guides/what-to-watch-tonight-uk">What to watch tonight in the UK</a></li>
                            <li><a href="/guides/netflix-under-90-minutes-uk">Netflix watches under 90 minutes in the UK</a></li>
                            <li><a href="/guides/comedy-on-netflix-uk">Comedy to watch on Netflix in the UK</a></li>
                        </ul>
                    </section>
                </div>}
            </main>

            {analyticsChoice === null && <aside className="analytics-notice" role="region" aria-label="Usage measurement choice"><div className="container"><p>Help us improve the picker? Allow usage measurement, including Google Analytics cookies, to understand visits and recommendation clicks. <a href="/privacy">Privacy details</a>.</p><div className="analytics-actions"><button type="button" className="analytics-allow" onClick={() => chooseAnalytics('yes')}>Allow measurement</button><button type="button" className="analytics-decline" onClick={() => chooseAnalytics('no')}>No thanks</button></div></div></aside>}

            {/* Footer */}
            <footer className="footer">
                <div className="container">
                    <nav className="footer-nav" aria-label="Site information">{path !== '/' && <a href="/">Home</a>}<a href="/about">About</a><a href="/privacy">Privacy</a></nav>
                    <p className="footer-text">
                        Powered by TMDB & Watchmode
                    </p>
                    <p className="footer-disclaimer">
                        This site is not affiliated with Netflix or any streaming service.
                    </p>
                </div>
            </footer>
        </div>
    );
}

export default App;
